import { validateEmail } from "@vcl";
import { $axios } from "@bloglovin/vue-framework";
import { defineStore } from "pinia";
import { useGeneralStore } from "%/stores/general-store";
import {
  ACCOUNT_DELETED_ERROR_CODE,
  EMAIL_FORM,
  TWO_FACTOR_FORM,
} from "@/constants/login-flow-constants";
import { getPasskeyAssertion } from "%/utils/webauthn";
import { links } from "%/enums/link-enums";
import { useSessionStorage } from "@vueuse/core";
import { useConfigurationStore } from "%/stores/configuration-store";
import { resolveEmailCheckOutcome } from "%/dynamic-component-editor/helpers/login-flow-steps";

export const useUserStore = defineStore("user-store", {
  state: () => ({
    userAuthEmail: useSessionStorage("userAuthEmail", ""),
    userFlowSteps: useSessionStorage("userFlowSteps", [EMAIL_FORM]),
    userFirstName: "",
    emailStatus: {
      requestedNewEmail: "",
      isEmailConfirmed: true,
      isEmailBeingChanged: false,
    },
    isLoading: false,
    loadingChangeFirstName: false,
    loadingChangeLastName: false,
    loadingChangeBio: false,
    loadingChangeAvatar: false,
    loadingChangeSplashImage: false,
    loginErrorMessage: "",
    signupErrorMessage: "",
    forgotPasswordErrorMessage: "",
    resetPasswordErrorMessage: "",
    verifyEmailErrorMessage: "",
    logoutErrorMessage: "",
    userDetailsErrorMessage: "",
    verifyUserErrorMessage: "",
    signUpNote: useSessionStorage("signUpNote", ""),
    resetPasswordError: "",
    invitingOrgId: 0,
    invitedUserId: 0,
    invitationToken: "",
    changeEmailError: "",
    isModerator: false,
    redirect: "",
    isEditingUserData: false,
    isFollowingOrgLoading: { },
    orgFollowStatus: {},
    loginWithInspirationRequestId: 0,
    loginWithInspirationRequestToken: "",
    mfaChallengeToken: "",
    mfaMethods: [],
    passkeyAssertionOptions: null,
    twoFactorErrorMessage: "",
  }),
  getters: {
    appId() {
      return useGeneralStore().appId;
    },
    hasValidUserAuthEmail() {
      return validateEmail(this.userAuthEmail);
    },
    currentUserFlowStep() {
      return this.userFlowSteps.at(-1);
    },
    previousUserFlowStep() {
      return this.userFlowSteps.at(-2);
    },
    usersApiV1Url() {
      return useGeneralStore().getLink(links.usersApiV1);
    },
    creatorApiV1Url() {
      return useGeneralStore().getLink(links.api);
    },
    fanApiV1Url() {
      return useGeneralStore().getLink(links.fanApiV1);
    },
    creatorApiV1() {
      return useGeneralStore().getLink(links.api);
    },
    userId() {
      return useGeneralStore().userId;
    },
    userData() {
      return useGeneralStore().userData;
    },
    emailData() {
      return this.userData.email_data ?? {};
    },
    userAvatar() {
      return useGeneralStore().userAvatar;
    },
    userSplashImage() {
      return useGeneralStore().userSplashImage;
    },
    firstName() {
      return this.userData.first_name ?? "";
    },
    bio() {
      return this.userData.bio ?? "";
    },
    lastName() {
      return this.userData.last_name ?? "";
    },
    currentEmail() {
      return this.emailData.email_address ?? "";
    },
    isEmailConfirmed() {
      return this.emailStatus.isEmailConfirmed;
    },
    requestedEmailAddress() {
      return this.emailStatus.requestedNewEmail;
    },
  },
  actions: {
    uploadAvatar(avatarFile) {
      if (this.loadingChangeAvatar) {
        return;
      }
      this.loadingChangeAvatar = true;
      let formData = new FormData();
      formData.append("avatar", avatarFile);
      $axios.post(`${this.creatorApiV1}/user/avatar/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }).then(response => {
        if (response.data.success) {
          useGeneralStore().setUserData(response.data.userData);
          this.loadingChangeAvatar = false;
        }
      });
    },
    uploadSplashImage(splashImageFile) {
      if (this.loadingChangeSplashImage) {
        return;
      }
      this.loadingChangeSplashImage = true;
      let formData = new FormData();
      formData.append("splash_image", splashImageFile);
      $axios.post(`${this.creatorApiV1}/user/splash-image/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }).then(response => {
        if (response.data.success) {
          useGeneralStore().setUserData(response.data.userData);
          this.loadingChangeSplashImage = false;
        }
      });
    },
    loginUser(password, appId = null) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      $axios
          .post("/user/login", {
            app_id: appId ?? this.appId,
            email: this.userAuthEmail,
            password: password,
            login_with_inspiration_request_id: this.loginWithInspirationRequestId,
            login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
            redirect: this.redirect,
          })
          .then(response => {
            if (response.data.result === "mfa_required") {
              this.mfaChallengeToken = response.data.challenge_token;
              this.mfaMethods = response.data.methods ?? [];
              this.passkeyAssertionOptions = response.data.passkey_assertion_options ?? null;
              this.twoFactorErrorMessage = "";
              this.userFlowSteps.push(TWO_FACTOR_FORM);
              return;
            }
            if (response.data.success) {
              window.location.href = this.redirect;
              return;
            }
            if (response.data.old_password) {
              this.loginErrorMessage = `You entered an old password, you changed it ${response.data.date_changed}`;
            } else {
              this.loginErrorMessage = "login failed for an unknown reason";
            }
          })
          .catch(error => {
            this.loginErrorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    completeSecondFactor(endpoint, payload) {
      this.isLoading = true;
      this.twoFactorErrorMessage = "";
      return $axios
          .post(endpoint, {
            challenge_token: this.mfaChallengeToken,
            login_with_inspiration_request_id: this.loginWithInspirationRequestId,
            login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
            redirect: this.redirect,
            ...payload,
          })
          .then(response => {
            if (response.data.success) {
              window.location.href = this.redirect;
              return;
            }
            this.twoFactorErrorMessage = "Verification failed, please try again.";
          })
          .catch(error => {
            this.twoFactorErrorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    verifyCode(code) {
      return this.completeSecondFactor("/user/login/verify-code", { code });
    },

    async verifyPasskey() {
      if (!this.passkeyAssertionOptions) {
        this.twoFactorErrorMessage = "No passkey is registered for this account.";
        return;
      }
      this.twoFactorErrorMessage = "";
      try {
        const assertion = await getPasskeyAssertion(this.passkeyAssertionOptions);
        return this.completeSecondFactor("/user/login/verify-passkey", assertion);
      } catch (error) {
        this.twoFactorErrorMessage = error?.message ?? "Passkey authentication was cancelled.";
      }
    },

    parseError(error) {
      const errorData = error?.response?.data ?? {};
      if (errorData.code === ACCOUNT_DELETED_ERROR_CODE && errorData.message) {
        return errorData.message;
      }
      if (errorData.message === "The password you entered is incorrect. Please try again.") {
        return "Incorrect password, please try again.";
      }
      return "Login failed, please try again.";
    },
    submitNewFirstName(firstName) {
      if (this.loadingChangeFirstName) {
        return;
      }
      this.loadingChangeFirstName = true;
      $axios.post(`${this.usersApiV1Url}/user/${this.userId}/first_name/change`, {
        first_name: firstName,
      }).then(response => {
        this.loadingChangeFirstName = false;
        const userData = useGeneralStore().userData;
        useGeneralStore().setUserData({
          ...userData,
          first_name: response.data.user_data.first_name,
        });
      }).catch(error => {
        if (error.message) {
          this.userDetailsErrorMessage = error.message;
        } else {
          this.userDetailsErrorMessage = "Submit new first name failed";
        }
        this.loadingChangeFirstName = false;
      });
    },
    submitNewLastName(lastName) {
      if (this.loadingChangeLastName) {
        return;
      }
      this.loadingChangeLastName = true;
      $axios.post(`${this.usersApiV1Url}/user/${this.userId}/last_name/change`, {
        last_name: lastName,
      }).then(response => {
        const userData = useGeneralStore().userData;
        useGeneralStore().setUserData({
          ...userData,
          last_name: response.data.user_data.last_name,
        });
        this.loadingChangeLastName = false;
      }).catch(error => {
        if (error.message) {
          this.userDetailsErrorMessage = error.message;
        } else {
          this.userDetailsErrorMessage = "Submit new last name failed";
        }
        this.loadingChangeLastName = false;
      });
    },
    submitNewBio(bio) {
      if (this.loadingChangeBio) {
        return;
      }
      this.loadingChangeBio = true;
      $axios.post(`${this.creatorApiV1}/user/bio`, {
        bio: bio,
      }).then(response => {
        this.loadingChangeBio = false;
        const userData = useGeneralStore().userData;
        useGeneralStore().setUserData({
          ...userData,
          bio: response.data.userData.bio,
        });
      }).catch(error => {
        if (error.message) {
          this.userDetailsErrorMessage = error.message;
        } else {
          this.userDetailsErrorMessage = "Submit new first name failed";
        }
        this.loadingChangeBio = false;
      });
    },
    submitNewPassword(currentPassword, newPassword) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      $axios.post(`${this.usersApiV1Url}/user/${this.userId}/password/change`, {
        current_password: currentPassword,
        new_password: newPassword,
      })
          .then(response => {
            if (!response.data.success) {
              this.userDetailsErrorMessage = response.data.message;
            }
          })
          .catch(error => {
            if (error.message) {
              this.userDetailsErrorMessage = error.response.data.message;
              return;
            }
            this.userDetailsErrorMessage = "Submit new password failed";
          }).finally(() => {
            this.isLoading = false;
          });
    },
    validateSignupFormData(firstName, lastName, email, password, confirmPassword) {
      if (!firstName.trim()) {
        this.signupErrorMessage = "First name is required";
        return false;
      }
      if (!lastName.trim()) {
        this.signupErrorMessage = "Last name is required";
        return false;
      }
      if (!email.trim()) {
        this.signupErrorMessage = "Email is required";
        return false;
      }
      if (!password.trim()) {
        this.signupErrorMessage = "Password is required";
        return false;
      }
      if (!confirmPassword.trim()) {
        this.signupErrorMessage = "Confirm password is required";
        return false;
      }
      if (password !== confirmPassword) {
        this.signupErrorMessage = "Passwords do not match";
        return false;
      }

      this.signupErrorMessage = "";
      return true;
    },
    signUpUser(firstName, lastName, email, password, appId = null) {
      const orgName = `${firstName} ${lastName}`;

      this.isLoading = true;
      $axios.post("/user/create", {
        org_name: orgName,
        first_name: firstName,
        last_name: lastName,
        email: email,
        password: password,
        app_id: appId ?? this.appId,
        login_with_inspiration_request_id: this.loginWithInspirationRequestId,
        login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
        redirect: this.redirect,
      }).then(() => {
        if (this.redirect) {
          if (this.redirect.startsWith("http")) {
            window.location.href = `${this.redirect}`;
          } else {
            window.location.href = `https://${window.location.hostname}${this.redirect}`;
          }
        } else {
          window.location.href = useGeneralStore().fanUrl;
        }
      }).catch(error => {
        if (error.message) {
          this.signupErrorMessage = error.message;
        } else {
          this.signupErrorMessage = "something unexpected went wrong during signup";
        }
      }).finally(() => {
        this.isLoading = false;
      });
    },

    authenticateLoginWithInspiration() {
      this.isLoading = true;
      $axios
          .post("/user/login-with-inspiration/authenticate", {
            login_with_inspiration_request_id: this.loginWithInspirationRequestId,
            login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
            redirect: this.redirect,
          })
          .then(response => {
            if (response.data.redirect) {
              window.location.href = response.data.redirect;
            } else {
              window.location.href = this.redirect;
            }
          })
          .catch(error => {
            if (error.message) {
              this.signupErrorMessage = error.message;
            } else {
              this.signupErrorMessage = "something unexpected went wrong during login";
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    forgotPassword(appId = null) {
      this.isLoading = true;
      $axios.post(
        "/user/forgot-password/initiate",
        {
          app_id: appId ?? this.appId,
          email: this.userAuthEmail,
          redirect: `${useGeneralStore().links.web}/login`,
        },
      ).catch(error => {
        if (error.message) {
          this.forgotPasswordErrorMessage = error.message;
        } else {
          this.forgotPasswordErrorMessage = "We are unable to reset your password at this time. Please try again later.";
        }
      }).finally(() => {
        this.isLoading = false;
      });
    },

    resetPassword(password, userId, token) {
      this.isLoading = true;
      $axios.post(
        `${this.usersApiV1Url}/user/${userId}/forgot-password/complete/${token}`,
        {
          password: password,
        },
      ).then(response => {
        if (response.data.success) {
          this.redirectTo("/login"); // USE RELATIVE REDIRECT
          return;
        }
        this.resetPasswordErrorMessage = "something unexpectedly went wrong resetting password";
      }).catch(error => {
        if (error.message) {
          this.resetPasswordErrorMessage = error.message;
        } else {
          this.resetPasswordErrorMessage = "something unexpectedly went wrong resetting password";
        }
      }).finally(() => {
        this.isLoading = false;
      });
    },

    verifyEmail(userId, emailId, token) {
      this.isLoading = true;
      $axios.post(
        `${this.usersApiV1Url}/user/verify-email/app/${this.appId}`,
        {
          user_id: userId,
          email_id: emailId,
          token: token,
        },
      ).then(response => {
        if (response.data.success) {
          this.redirectTo("/"); // USE RELATIVE REDIRECT
          return;
        }
        this.verifyEmailErrorMessage = "Something went wrong verifying email";
      }).catch(error => {
        if (error.message) {
          this.verifyEmailErrorMessage = error.message;
        } else {
          this.verifyEmailErrorMessage = "something unexpectedly went wrong resetting password";
        }
      }).finally(() => {
        this.isLoading = false;
      });
    },

    logoutUser(redirectUri = "/") {
      this.isLoading = true;
      $axios.post(`${this.fanApiV1Url}/user/logout`).then(response => {
        if (response.data.success) {
          if (redirectUri.startsWith("https://")) {
            window.location.href = redirectUri;
          } else {
            window.location.href = useGeneralStore().links.fanUrl + redirectUri;
          }
          return;
        }
        this.logoutErrorMessage = "something went wrong during logout";
      }).catch(error => {
        if (error.message) {
          this.logoutErrorMessage = error.message;
        } else {
          this.logoutErrorMessage = "something went wrong during logout";
        }
      }).finally(() => {
        this.isLoading = false;
      });
    },

    async verifyUserExists(email, appId = null) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      if (appId == null) {
        appId = this.appId;
      }
      const encodedEmail = encodeURIComponent(email);
      this.verifyUserErrorMessage = "";
      this.signUpNote = "";
      const verifyUserUri = `${this.usersApiV1Url}/user/app/${appId}/exists?email=${encodedEmail}`;
      return $axios.get(verifyUserUri).then(response => {
        this.userAuthEmail = email;

        const deleted = response.data.deleted === true;
        const exists = response.data.exists !== false && !deleted;
        if (exists) {
          this.userFirstName = response.data.first_name;
        }
        const { step, signUpNote } = resolveEmailCheckOutcome({
          exists,
          deleted,
          canSignUp: useConfigurationStore().canSignUp,
        });
        this.signUpNote = signUpNote;
        this.userFlowSteps.push(step);

        return { email, exists, deleted };
      }).catch(error => {
        if (error.message) {
          this.verifyUserErrorMessage = error.message;
        } else {
          this.verifyUserErrorMessage = "something went wrong checking to see if user has account";
        }
      }).finally(() => {
        this.isLoading = false;
      });
    },

    fetchEmailStatus() {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      $axios.get(`${this.usersApiV1Url}/user/${this.userId}/email/status`)
          .then(response => {
            if (response.data.success) {
              let emailStatus = response.data.email_status;
              if (emailStatus.isEmailConfirmed) {
                emailStatus.requestedNewEmail = this.emailData.email_address;
              }

              this.emailStatus = emailStatus;
            }
            if (!response.data.success) {
              this.userDetailsErrorMessage = response.data.message;
            }
            this.isLoading = false;
          })
          .catch(error => {
            if (error.message) {
              this.userDetailsErrorMessage = error.message;
            } else {
              this.userDetailsErrorMessage = "Check email status failed.";
            }
          });
    },

    cancelEmailChange() {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      $axios.post(`${this.usersApiV1Url}/user/${this.userData.user_id}/email/cancel-change`)
          .then(response => {
            if (!response.data.success) {
              this.userDetailsErrorMessage = response.data.message;
              this.isLoading = false;
              return;
            }
            this.emailStatus.isEmailConfirmed = true;
            this.isLoading = false;
          })
          .catch(error => {
            if (error.message) {
              this.userDetailsErrorMessage = error.message;
            } else {
              this.userDetailsErrorMessage = "Cancel email change process failed.";
            }
          });
    },
    redirectTo(href, fallbackHref) {
      return useGeneralStore().redirectTo(href, fallbackHref);
    },
    async followOrg(orgIdBeingFollowed, visible = true) {
      this.isFollowingOrgLoading[orgIdBeingFollowed] = true;
      await this.apiPost(
        "/org/follow",
        {
          org_id_being_followed: orgIdBeingFollowed,
          visible: visible ? 1 : 0,
        },
        response => {
          this.orgFollowStatus[orgIdBeingFollowed] = response.status;
          this.isFollowingOrgLoading[orgIdBeingFollowed] = false;
        },
        () => {},
        () => {
          this.isFollowingOrgLoading[orgIdBeingFollowed] = false;
        },
      );
    },
    async unfollowOrg(orgIdBeingFollowed) {
      this.isFollowingOrgLoading[orgIdBeingFollowed] = true;
      await this.apiPost(
        "/org/unfollow",
        {
          org_id_being_followed: orgIdBeingFollowed,
        },
        response => {
          this.orgFollowStatus[orgIdBeingFollowed] = response.status;
          this.isFollowingOrgLoading[orgIdBeingFollowed] = false;
        },
        () => {},
        () => {
          this.isFollowingOrgLoading[orgIdBeingFollowed] = false;
        },
      );
    },
    acceptInvitation() {
      this.apiPost("/user/invite/accept", {}, () => {
        this.redirectTo("/");
      });
    },
    signUpAndAcceptInvitation(password) {
      this.apiPost(
        "/user/invitation/publication/accept",
        {
          user_id: this.invitedUserId,
          first_name: this.userFirstName,
          last_name: this.userLastName,
          password: password,
          token: this.invitationToken,
          publication_id: this.invitedPublicationId,
        },
        () => {
          window.location.href = this.redirect;
        },
      );
    },
    finalizeCollaboratorOffPlatformInvite(
      orgId,
      userId,
      firstName,
      lastName,
      orgName,
      password,
      token,
    ) {
      this.isLoading = true;
      this.apiPost(
        `/user/${userId}/org/${orgId}/invite/${token}/finalize`,
        {
          first_name: firstName,
          last_name: lastName,
          org_name: orgName,
          password: password,
        },
        () => {
          window.location.href = "/";
        },
        error => {
          this.signupErrorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Something unexpected went wrong during registration";
        },
        () => {
          this.isLoading = false;
        },
      );
    },
  },
});
